/** Phase 3A.1 — AIRunBadge: model + cost + latency + quality chip row.
 * Props: { model, cost_usd, latency_ms, quality_score, compact?, fullProvenance? }
 */
import { Badge } from "../ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";

function qualityColor(q) {
  if (q == null || isNaN(q)) return "outline";
  if (q >= 0.75) return "default";    // emerald-ish (default = primary)
  if (q >= 0.5) return "secondary";   // amber-ish (we'll style)
  return "destructive";
}
function qualityClass(q) {
  if (q == null || isNaN(q)) return "";
  if (q >= 0.75) return "bg-emerald-600 hover:bg-emerald-600 text-white border-transparent";
  if (q >= 0.5)  return "bg-amber-500 hover:bg-amber-500 text-white border-transparent";
  return "bg-red-600 hover:bg-red-600 text-white border-transparent";
}

function fmtCost(c) {
  if (c == null) return "—";
  if (c < 0.01) return `$${c.toFixed(4)}`;
  return `$${c.toFixed(3)}`;
}
function fmtLatency(ms) {
  if (ms == null) return "—";
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}
function fmtQuality(q) {
  return (q == null || isNaN(q)) ? "—" : Number(q).toFixed(2);
}

export default function AIRunBadge({
  model, cost_usd, latency_ms, quality_score, compact, fullProvenance,
}) {
  const qClass = qualityClass(quality_score);
  const provLines = fullProvenance
    ? Object.entries(fullProvenance).filter(([k]) => k !== "asset_id")
    : [];

  const QualityChip = (
    <Badge
      data-testid="ai-run-badge-quality"
      className={`font-mono text-[11px] ${qClass}`}
    >
      Q {fmtQuality(quality_score)}
    </Badge>
  );

  const content = compact ? (
    <div className="inline-flex items-center gap-1.5">
      <Badge variant="outline" className="font-mono text-[11px]" data-testid="ai-run-badge-model-compact">
        {model || "mocked"}
      </Badge>
      {QualityChip}
    </div>
  ) : (
    <div className="inline-flex items-center gap-1.5" data-testid="ai-run-badge">
      <Badge variant="outline" className="font-mono text-[11px]" data-testid="ai-run-badge-model">
        {model || "mocked"}
      </Badge>
      <Badge variant="outline" className="font-mono text-[11px]" data-testid="ai-run-badge-cost">
        {fmtCost(cost_usd)}
      </Badge>
      <Badge variant="outline" className="font-mono text-[11px]" data-testid="ai-run-badge-latency">
        {fmtLatency(latency_ms)}
      </Badge>
      {QualityChip}
    </div>
  );

  if (!fullProvenance) return content;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button type="button" className="cursor-help" data-testid="ai-run-badge-trigger">
            {content}
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-sm" data-testid="ai-run-badge-tooltip">
          <div className="text-[11px] font-mono space-y-1">
            <div className="font-semibold mb-1 text-xs">Provenance</div>
            {provLines.map(([k, v]) => (
              <div key={k} className="flex gap-2">
                <span className="text-muted-foreground">{k}:</span>
                <span className="break-all">{
                  v == null ? "—" :
                  typeof v === "object" ? JSON.stringify(v) :
                  String(v)
                }</span>
              </div>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
